import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import * as tasksActions from '../actionCreators';
import { Grid } from '@mui/material';
import TaskListComponent from './TaskListComponent';
import TaskFiltersComponent from './TaskFiltersComponent';
import { Card } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { changeValueInArray } from 'utils/functions';
import TaskReasonsComponent from './TaskReasonsComponent';
import ModalTaskResponsibleSelect from './ModalTaskResponsibleSelect';
import documentTypeIds from 'constants/documentTypeIds';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { taskStatesDict, taskPrioritiesDict, taskFilterOptionsDict } from '../constants';

const initialPager = {
  page: 1,
  pageSize: 10
};

let currentTask;

const useFilters = initialValue => {
  //custom hook
  const [filterSelections, setFilterSelections] = useState(initialValue);

  const onChangeFilters = filterSelections => {
    setFilterSelections(filterSelections);
  };

  return [filterSelections, onChangeFilters];
};

const TaskListContainer = ({ pushHistory }) => {
  const { t } = useTranslation();
  const {
    tasks,
    tasksLoading,
    taskOptions,
    tasksResponsibles,
    taskStates,
    taskStatuses,
    taskTypes,
    clientCategories,
    tasksConfig,
    tasksIsChanged,
    taskResponsiblesForSingleTask
  } = useSelector(state => state.tasksData, shallowEqual);
  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const dispatch = useDispatch();

  const translatedOptions = taskOptions
    .map(value => {
      return { ...value, name: t('TaskOption.' + value.name) };
    })
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

  let translatedStatuses = taskStatuses
    .map(value => {
      return { ...value, name: t('TaskStatus.' + value.name) };
    })
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

  const translatedStates = taskStates
    .map(value => {
      return { ...value, name: t('TaskState.' + value.name) };
    })
    .sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }
      return 0;
    });

  const taskPriorities = useMemo(
    () =>
      Object.values(taskPrioritiesDict)
        .map(value => ({ value, label: t('TaskPriority.' + value) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [t]
  );

  const getTranslatedStatuses = useCallback(
    stateValue => {
      if (stateValue === undefined || stateValue === null) return translatedStatuses; // Show all statuses

      const showActiveStatuses = stateValue === taskStatesDict.ACTIVE;
      return translatedStatuses.filter(x => x.active === showActiveStatuses);
    },
    [translatedStatuses]
  );

  const defaultOption = translatedOptions.length > 0 ? translatedOptions[0] : null;
  const defaultState = taskStates.find(opt => opt.name === taskStatesDict.ACTIVE);

  const defaultFilteredStatuses = getTranslatedStatuses(defaultState?.name);
  const defaultStatus = defaultFilteredStatuses.length > 0 ? defaultFilteredStatuses[0] : null;
  const isDataLoaded = !tasksLoading && taskTypes?.length > 0 && clientCategories?.length > 0 && taskStates?.length > 0;

  const defaultFilters = useMemo(
    () => ({
      option: defaultOption,
      responsible: {},
      state: defaultState,
      status: defaultStatus,
      taskTypes: taskTypes,
      clientCategory: null,
      isHighPriority: null
    }),
    [defaultOption, defaultState, defaultStatus, taskTypes]
  );

  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [filterSelections, onChangeFilters] = useFilters(defaultFilters);

  const [pager, setPager] = useState(initialPager);
  const [showCommentsDialog, setShowCommentsDialog] = useState(false);
  const [currentConfig, setCurrentConfig] = useState(null);
  const [showResponsibleModal, setShowResponsibleModal] = useState(false);

  const filteredStatuses = useMemo(
    () => (filterSelections?.state?.name ? getTranslatedStatuses(filterSelections?.state?.name) : []),
    [filterSelections?.state?.name, getTranslatedStatuses]
  );
  //#endregion

  useEffect(() => {
    if (isDataLoaded && !filtersInitialized) {
      onChangeFilters(defaultFilters);
      setFiltersInitialized(true);
    }
  }, [isDataLoaded, defaultFilters, filtersInitialized, onChangeFilters]);

  const reasonsUpdater = useCallback(
    (reasonId, selected) => ({
      ...currentConfig,
      reasons: changeValueInArray(currentConfig.reasons, 'id', reasonId, 'selected', selected)
    }),
    [currentConfig]
  );

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize'
        ? {
            ...pager,
            [prop]: value,
            page: 1
          }
        : {
            ...pager,
            [prop]: value
          };
    },
    [pager]
  );

  const loadTasksEffect = () => {
    if (tasksIsChanged) {
      loadTasks(filterSelections, pager);
    }
  };

  useEffect(() => {
    dispatch(tasksActions.loadTaskOptions());
    dispatch(tasksActions.loadClientCategories());
    dispatch(tasksActions.loadTaskTypes());
    dispatch(tasksActions.loadTaskResponsibles());
    dispatch(tasksActions.loadTaskStates());
    dispatch(tasksActions.loadTaskStatuses());
    dispatch(tasksActions.loadTasksCommentsConfig());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isDataLoaded) loadTasks(filterSelections, pager);
  }, [filterSelections, pager]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTasksEffect();
  }, [tasksIsChanged]); // eslint-disable-line react-hooks/exhaustive-deps

  const createPostTaskBody = useCallback(
    (filterSelections, pager) => ({
      filterByCurrentUser:
        filterSelections.option?.name === t('TaskOption.' + taskFilterOptionsDict.USER_TASKS)
          ? true
          : filterSelections.option?.name === t('TaskOption.' + taskFilterOptionsDict.TEAM_TASKS)
          ? false
          : null,
      taskTypes: filterSelections.taskTypes?.length > 0 ? filterSelections.taskTypes : undefined,
      isActive: filterSelections.state ? filterSelections.state.id : undefined,
      statusId: filterSelections.status ? filterSelections.status.id : undefined,
      responsibleId: filterSelections.responsible ? filterSelections.responsible.id : undefined,
      clientCategory: filterSelections.clientCategory ?? undefined,
      isHighPriority:
        filterSelections.isHighPriority?.value === taskPrioritiesDict.HIGH
          ? true
          : filterSelections.isHighPriority?.value === taskPrioritiesDict.NORMAL
          ? false
          : null,
      page: pager.page,
      pageSize: pager.pageSize
    }),
    [t]
  );

  const loadTasks = useCallback(
    (filterSelections, pager) => {
      const requestBody = createPostTaskBody(filterSelections, pager);
      dispatch(tasksActions.loadDashboardTasks(requestBody));
    },
    [createPostTaskBody, dispatch]
  );

  const reasonChange = useCallback(
    reasonId => event => {
      setCurrentConfig(reasonsUpdater(reasonId, event.target.checked));
    },
    [reasonsUpdater]
  );

  const onChangePage = useCallback(page => {
    setPager(prevPager => ({ ...prevPager, page: ++page }));
  }, []);

  const onChangeRowsPerPage = useCallback(
    pageSize => {
      setPager(pagerUpdater('pageSize', pageSize));
    },
    [pagerUpdater]
  );

  const handleFilterValueChange = useCallback(
    name => value => {
      if (name === 'state') {
        let state = null;
        let status = null;
        if (value !== null && value !== undefined) {
          state = translatedStates.find(s => s.id === value);
          status = getTranslatedStatuses(state?.name)?.[0] ?? null;
        }
        onChangeFilters({
          ...filterSelections,
          [name]: state,
          status: status
        });
      } else if (name === 'option' || name === 'taskTypes' || name === 'clientCategory' || name === 'isHighPriority') {
        onChangeFilters({ ...filterSelections, [name]: value });
      } else {
        onChangeFilters({ ...filterSelections, [name]: { id: value } });
      }
      setPager({ ...pager, page: 1 });
    },
    [filterSelections, getTranslatedStatuses, onChangeFilters, pager, translatedStates]
  );

  const handleTogglePriority = useCallback(
    task => () => {
      const newPriority = !task.isHighPriority;
      dispatch(tasksActions.updateTaskPriority(task.taskId, task.processId, newPriority));
    },
    [dispatch]
  );

  const closeReasonsDialog = useCallback(() => {
    setShowCommentsDialog(false);
  }, []);

  const onRefresh = useCallback(() => {
    loadTasks(filterSelections, pager);
  }, [filterSelections, loadTasks, pager]);

  const onOpenOffer = useCallback(
    task => () => {
      if (
        task.documentType.id === documentTypeIds.Offer ||
        task.documentType.id === documentTypeIds.FacilityFrameOffer ||
        task.documentType.id === documentTypeIds.OfferModification
      )
        pushHistory(`/offers/${task.documentId}/${task.siteId}`);
      if (task.documentType.id === documentTypeIds.Folder) pushHistory(`/folders/${task.documentId}/${task.siteId}`);
    },
    [pushHistory]
  );

  const onPauseTask = useCallback(
    task => () => {
      currentTask = task;
      const taskConfigPaused = tasksConfig.find(a => a.stateId === 3); // paused state id
      if (taskConfigPaused) {
        setShowCommentsDialog(true);
        setCurrentConfig(taskConfigPaused);
      } else {
        dispatch(tasksActions.pauseTask(task, '', []));
      }
    },

    [dispatch, tasksConfig]
  );

  const onSaveReasons = useCallback(
    (task, comment, reasonsIds) => {
      if (task && task.status.id === 2) {
        dispatch(tasksActions.pauseTask(task, comment, reasonsIds));
        closeReasonsDialog();
      }
    },
    [closeReasonsDialog, dispatch]
  );

  const onStartTask = useCallback(
    task => () => {
      dispatch(tasksActions.startTask(task));
    },
    [dispatch]
  );

  const onOpenModal = useCallback(
    task => () => {
      currentTask = task;
      setShowResponsibleModal(true);
      dispatch(tasksActions.loadResponsiblesForSingleTask(task.taskId, ''));
    },
    [dispatch]
  );

  const onCloseModal = useCallback(() => {
    currentTask = null;
    setShowResponsibleModal(false);
  }, []);

  const onTaskResponsibleSave = task => responsibleId => () => {
    dispatch(tasksActions.updateTask(task, responsibleId));
    onCloseModal();
  };

  const onSaveMeAsResponsible = useCallback(
    task => () => {
      dispatch(tasksActions.updateTask(task, userInfo.userId));
      onCloseModal();
    },
    [dispatch, onCloseModal, userInfo.userId]
  );

  const getReasonsTask = task => {
    return currentConfig ? (
      <TaskReasonsComponent
        task={task}
        currentConfig={currentConfig}
        onCloseReasonsDialog={closeReasonsDialog}
        showCommentsDialog={showCommentsDialog}
        onSaveReasons={onSaveReasons}
        reasonChange={reasonChange}
      />
    ) : null;
  };

  const getResponsibleModal = task => {
    return (
      <ModalTaskResponsibleSelect
        task={task}
        onAssignResponsible={onTaskResponsibleSave(task)}
        onCloseModal={onCloseModal}
        onAssignToMeResponsible={onSaveMeAsResponsible}
        taskResponsibles={taskResponsiblesForSingleTask?.length !== 0 ? taskResponsiblesForSingleTask : tasksResponsibles}
      />
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card iconColor='primary' title={t('Dashboard.Tasks')} subheader={<>{t('Dashboard.CurrentTasks')}</>}>
          <>
            <TaskFiltersComponent
              options={taskOptions}
              states={taskStates}
              taskTypes={taskTypes}
              taskPriorities={taskPriorities}
              clientCategories={clientCategories}
              statuses={filteredStatuses}
              responsibles={tasksResponsibles}
              filterSelections={filterSelections}
              onFilterValueChanged={handleFilterValueChange}
              translatedOptions={translatedOptions}
              translatedStates={translatedStates}
            />
            {tasksLoading ? (
              <WrappedFakeText lines={7} my={2} />
            ) : tasks && tasks.values ? (
              <TaskListComponent
                tasks={tasks.values}
                onOpenOffer={onOpenOffer}
                onAssignToMe={onSaveMeAsResponsible}
                onPauseTask={onPauseTask}
                onStartTask={onStartTask}
                onOpenModal={onOpenModal}
                onTogglePriority={handleTogglePriority}
                userInfo={userInfo}
              />
            ) : (
              <div></div>
            )}
            {tasks && tasks.totalCount >= 0 ? (
              <PaginationWrapper
                count={tasks.totalCount}
                pageSize={pager.pageSize}
                page={pager.page}
                onRowsPerPageChange={onChangeRowsPerPage}
                onPageChange={onChangePage}
                onRefresh={onRefresh}
              />
            ) : (
              <div></div>
            )}
            {showCommentsDialog ? getReasonsTask(currentTask) : null}
            {showResponsibleModal ? getResponsibleModal(currentTask) : null}
          </>
        </Card>
      </Grid>
    </Grid>
  );
};

TaskListContainer.propTypes = {
  pushHistory: PropTypes.func.isRequired
};

export default TaskListContainer;
